import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Utensils } from "lucide-react";
import UploadImage from "@/components/UploadImage";
import UploadFullMenu from "@/components/UploadFullMenu";
import { fileUrl } from "@/api";

const empty = {
  name: "",
  price: "",
  description: "",
  category: "",
  cuisine: "",
  image_url: "",
  available: true,
};

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [itemsRes, vendorRes] = await Promise.all([
        api.get("/menu-items"),
        api.get("/vendors/me"),
      ]);
      setItems(itemsRes.data || []);
      setVendor(vendorRes.data || null);
    } catch (err) {
      console.error("[Menu] load failed:", err?.message || err);
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onMenuUploaded = async ({ file_id, url, content_type, filename }) => {
    const payload = {
      full_menu_file_id: file_id,
      full_menu_url: url,
      full_menu_content_type: content_type,
      full_menu_filename: filename,
      full_menu_uploaded_at: new Date().toISOString(),
    };
    const { data } = await api.put("/vendors/me", payload);
    setVendor(data);
  };

  const onMenuRemoved = async () => {
    await api.delete("/vendors/me/full-menu");
    const { data } = await api.get("/vendors/me");
    setVendor(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
      };
      await api.post("/menu-items", payload);
      toast.success("Menu item added");
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add item");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/menu-items/${id}`);
      toast.success("Item removed");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (_e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="menu-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Menu</h1>
        <p className="text-slate-500 mt-1 text-sm">Showcase what you're serving today.</p>
      </div>

      <UploadFullMenu
        menu={vendor}
        onUploaded={onMenuUploaded}
        onRemoved={onMenuRemoved}
      />

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="menu-form">
        <h2 className="text-base font-semibold">Add menu item</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="m-name">Name</Label>
            <Input id="m-name" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="menu-name" />
          </div>
          <div>
            <Label htmlFor="m-price">Price (USD)</Label>
            <Input id="m-price" type="number" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} data-testid="menu-price" />
          </div>
          <div>
            <Label htmlFor="m-category">Category</Label>
            <Input id="m-category" value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="Mains, Sides..." data-testid="menu-category" />
          </div>
          <div>
            <Label htmlFor="m-cuisine">Cuisine</Label>
            <Input id="m-cuisine" value={form.cuisine} onChange={(e) => update("cuisine", e.target.value)} placeholder="Mexican, Korean..." data-testid="menu-cuisine" />
          </div>
        </div>
        <div>
          <Label htmlFor="m-desc">Description</Label>
          <Textarea id="m-desc" rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="menu-description" />
        </div>
        <div>
          <Label>Image</Label>
          <div className="mt-2">
            <UploadImage value={form.image_url} onChange={(v) => update("image_url", v)} testid="menu-image" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" className="ff-btn-primary" disabled={saving} data-testid="menu-add">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={14} className="mr-1" />} Add item
          </Button>
        </div>
      </form>

      <div className="space-y-3">
        <h2 className="text-base font-semibold">Your menu · {items.length} items</h2>
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-slate-500 text-sm bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center" data-testid="menu-empty">
            <Utensils className="mx-auto mb-2 text-slate-400" />
            No items yet. Add your first dish above.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="menu-list">
            {items.map((it) => (
              <li key={it.id} className="bg-white border border-slate-200 rounded-xl p-4 flex gap-3" data-testid={`menu-item-${it.id}`}>
                {it.image_url ? (
                  <img src={fileUrl(it.image_url)} alt={it.name} className="h-20 w-20 rounded-md object-cover flex-shrink-0" />
                ) : (
                  <div className="h-20 w-20 rounded-md bg-slate-100 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{it.name}</div>
                      <div className="text-xs text-slate-500 truncate">{it.category} {it.cuisine && `· ${it.cuisine}`}</div>
                    </div>
                    <div className="text-[#E8650A] font-bold whitespace-nowrap">${Number(it.price).toFixed(2)}</div>
                  </div>
                  {it.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{it.description}</p>}
                  <div className="mt-2">
                    <Button variant="ghost" size="sm" onClick={() => remove(it.id)} data-testid={`menu-delete-${it.id}`}>
                      <Trash2 size={14} className="mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
